import React, { Component } from 'react';
import type { ReactNode } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isUserAdmin, isUserOwner } from '../../helpers';
import { addCategory, editCategory, removeCategory } from '../../redux/actions/categories';
import { CATEGORY_MODAL, MARKETING_MODAL, PAYROLL_MODAL } from '../../redux/reducers/modal';
import CategoriesContent from '../blocks/categories/CategoriesContent';
import CategoryModal from '../blocks/categories/CategoriesModal';
import MarketingStatsModal from '../blocks/marketing/MarketingStatsModal';
import ReduxModal from '../blocks/modal/ReduxModal';
import MainNav from '../blocks/navigation/MainNav';
import PayrollGeneratorModal from '../blocks/payroll/PayrollGeneratorModal';
import type { ProjectCategory, User } from '../../types';

type Props = {
  currentUser: User,
  categories: Array<ProjectCategory>,
  onAddCategory: (formData: object) => Promise<void>,
  onEditCategory: (formData: object) => Promise<void>,
  onRemoveCategory: (category: ProjectCategory) => void,
  onOpenModal: (payload: { id: string }) => void,
  onCloseModal: (payload: { id: string }) => void,
  navigate: (path: string) => void,
};

type State = {
  modal: {
    edit: boolean,
    saveButtonText: string,
    title: string,
    category?: ProjectCategory,
  }
};

class Categories extends Component<Props, State> {
  state = {
    modal: {
      edit: false,
      saveButtonText: '',
      title: '',
      category: undefined,
    },
  };

  handleModalClose = () => {
    const { onCloseModal } = this.props;
    onCloseModal({ id: CATEGORY_MODAL });
    this.setState({
      modal: {
        edit: false,
        saveButtonText: '',
        title: '',
        category: undefined,
      },
    });
  };

  handleOpenCategoryModal = () => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: CATEGORY_MODAL });
    this.setState({
      modal: {
        edit: false,
        saveButtonText: 'Create Category',
        title: 'New Category',
        category: undefined,
      },
    });
  };

  handleOpenEditCategoryModal = (category: ProjectCategory) => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: CATEGORY_MODAL });
    this.setState({
      modal: {
        edit: true,
        saveButtonText: 'Save Changes',
        title: 'Edit Category',
        category,
      },
    });
  };

  handleCategorySubmit = (formData: object) => {
    const { onAddCategory, onEditCategory } = this.props;
    const { modal: { edit, category } } = this.state;

    if (edit && category) {
      onEditCategory({ ...category, ...formData }).then(this.handleModalClose);
    } else {
      onAddCategory(formData).then(this.handleModalClose);
    }
  };

  handlePayrollGeneratorClick = () => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: PAYROLL_MODAL });
  };

  handlePayrollModalClose = () => {
    const { onCloseModal } = this.props;
    onCloseModal({ id: PAYROLL_MODAL });
  };

  handleMarketingStatsClick = () => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: MARKETING_MODAL });
  };

  handleMarketingModalClose = () => {
    const { onCloseModal } = this.props;
    onCloseModal({ id: MARKETING_MODAL });
  };


  render(): ReactNode {
    const { modal } = this.state;
    const {
      currentUser,
      categories,
      navigate,
      onRemoveCategory,
    } = this.props;

    if (!currentUser) {
      return null;
    }

    const isOwner = isUserOwner(currentUser);
    const isAdmin = isUserAdmin(currentUser);

    if (!isOwner && !isAdmin) {
      return null;
    }

    return (
      <div className="container-view view-wrapper categories-view">
        <MainNav
          currentUser={currentUser}
          navigate={navigate}
          onPayrollGeneratorClick={this.handlePayrollGeneratorClick}
          onMarketingStatsClick={this.handleMarketingStatsClick}
        />
        <CategoriesContent
          categories={categories}
          onOpenAddCategoryModal={this.handleOpenCategoryModal}
          onOpenEditCategoryModal={this.handleOpenEditCategoryModal}
          onRemoveCategory={onRemoveCategory}
        />
        <ReduxModal id={CATEGORY_MODAL}>
          <CategoryModal
            title={modal.title}
            saveButtonText={modal.saveButtonText}
            onSubmit={this.handleCategorySubmit}
            onClose={this.handleModalClose}
            category={modal.category}
          />
        </ReduxModal>
        <ReduxModal id={PAYROLL_MODAL}>
          <PayrollGeneratorModal
            onCloseModal={this.handlePayrollModalClose}
          />
        </ReduxModal>
        <ReduxModal id={MARKETING_MODAL}>
          <MarketingStatsModal
            onCloseModal={this.handleMarketingModalClose}
          />
        </ReduxModal>
      </div>
    );
  }
}

const withNavigation = (WrappedComponent) => props => {
  const navigate = useNavigate();

  return <WrappedComponent {...props} navigate={navigate} />;
};

const mapStateToProps = (state) => ({
  currentUser: state.currentUser,
  categories: state.categories,
});

const mapDispatchToProps = (dispatch: any) => ({
  onOpenModal: (modalData: object) => dispatch({ type: 'OPEN_MODAL', payload: modalData }),
  onCloseModal: (modalData: object) => dispatch({ type: 'CLOSE_MODAL', payload: modalData }),
  onAddCategory: (formData: object) => dispatch(addCategory(formData)),
  onEditCategory: (category: ProjectCategory) => dispatch(editCategory(category)),
  onRemoveCategory: (category: ProjectCategory) => dispatch(removeCategory(category)),
});

const ConnectedCategories = withNavigation(connect(mapStateToProps, mapDispatchToProps)(Categories))

export default ConnectedCategories;
